import {
  type AnyBlock,
  type ARIAFilterSuggestion,
  capitalize,
  DEFAULT_FILTER_PRIORITY,
  type FilterBlock,
  type FilterConfig,
  type FilterKey,
  type FilterProvider,
  FilterProviderType,
  type FilterQuery,
  type FilterValueData,
  FilterValueType,
  getFilterValue,
  type IndexedBlockValueItem,
  MAXIMUM_SUGGESTIONS_TO_SHOW,
  type MutableFilterBlock,
  type SuppliedFilterProviderOptions,
  type ValueRowProps,
} from '@github-ui/filter-core'
import {AsyncFilterProvider} from '@github-ui/filter-core/providers'
import {reactFetchJSON} from '@github-ui/verified-fetch'

import {debounceAsync} from '../../utils/debounce-async'

const SUGGESTION_DEBOUNCE_MS = 200

/**
 * Base provider for the workflow-runs suggestion endpoints (`Actions::{Workflows,Events,Statuses,
 * Branches,Actors}Controller`). Subclasses only map a response item to its value and display name.
 */
export abstract class WorkflowRunsAsyncFilterProvider<T> extends AsyncFilterProvider<T> implements FilterProvider {
  // Aborting only cancels the browser's interest; the request is still sent and Rails still serves
  // it. Without debouncing, a slow endpoint degrades to one request per keystroke — the moment the
  // extra load hurts most — so coalesce keystrokes before hitting the server.
  #fetchDataDebounced = debounceAsync(
    (apiURL: string, abortController?: AbortController) => this.fetchData(apiURL, abortController),
    SUGGESTION_DEBOUNCE_MS,
  )

  constructor(filterKey: FilterKey, endpoint: string, options?: SuppliedFilterProviderOptions) {
    super(filterKey, options)
    this.suggestionEndpoint = endpoint
    this.type = FilterProviderType.Select
  }

  async getSuggestions(
    filterQuery: FilterQuery,
    filterBlock: AnyBlock | MutableFilterBlock,
    _config: FilterConfig,
    caretIndex?: number | null,
  ) {
    return this.processSuggestions(filterQuery, filterBlock, this.#processSuggestion.bind(this), caretIndex)
  }

  /**
   * The base class fetches with bare `fetch`, which omits the `GitHub-Is-React` header that
   * attributes app-server requests in Datadog's `request.dist.referrer` metric. Route through
   * `reactFetchJSON` so these five endpoints stay observable, matching the rest of this package.
   */
  override async fetchData(apiURL: string, abortController?: AbortController): Promise<Response | undefined> {
    try {
      return await reactFetchJSON(apiURL, {method: 'GET', signal: abortController?.signal})
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return undefined
      throw error
    }
  }

  /** These endpoints return a bare JSON array rather than the `{key: [...]}` envelope the base class expects. */
  override async fetchSuggestions(value: string): Promise<T[] | null> {
    try {
      if (this.suggestionCache.has(value)) return this.suggestionCache.get(value) ?? []
      if (!this.suggestionEndpoint) return null

      const endpoint = new URL(this.suggestionEndpoint, window.location.origin)
      endpoint.search = new URLSearchParams({q: value}).toString()

      const response = await this.#fetchDataDebounced(endpoint.toString(), this.abortSuggestionsController)
      if (!response?.ok) return null

      const items = (await response.json()) as T[]
      if (!Array.isArray(items)) return null

      // A short response proves the result set is complete, letting narrower prefixes be
      // filtered locally instead of refetching on every keystroke.
      if (items.length < MAXIMUM_SUGGESTIONS_TO_SHOW) this.completeResultSetQueries.add(value)
      this.suggestionCache.set(value, items)
      return items
    } catch {
      return null
    }
  }

  /** Any raw text is valid: unmatched values simply return no runs, matching the replaced key-only providers. */
  override async validateFilterBlockValues(
    _filterQuery: FilterQuery,
    _block: FilterBlock,
    values: IndexedBlockValueItem[],
  ): Promise<IndexedBlockValueItem[]> {
    return values.map(value => ({...value, valid: true}))
  }

  getValueRowProps(value: FilterValueData): ValueRowProps {
    return {
      text: value.displayName ?? getFilterValue(value.value) ?? '',
      description: value.description,
      descriptionVariant: 'inline',
    }
  }

  #processSuggestion(item: T): ARIAFilterSuggestion {
    const {value, displayName, description} = this.toSuggestion(item)
    return {
      type: FilterValueType.Value,
      value,
      displayName,
      description,
      inlineDescription: description !== undefined,
      ariaLabel: [displayName, description, this.displayName].filter(Boolean).join(', '),
      priority: DEFAULT_FILTER_PRIORITY,
    }
  }

  protected abstract toSuggestion(item: T): {value: string; displayName: string; description?: string}
}

export interface WorkflowRunWorkflowSuggestion {
  name: string
  pinned: boolean
}

type WorkflowRunWorkflowSuggestionResponse = string | WorkflowRunWorkflowSuggestion

export class WorkflowSuggestionsFilterProvider extends WorkflowRunsAsyncFilterProvider<WorkflowRunWorkflowSuggestionResponse> {
  protected toSuggestion(item: WorkflowRunWorkflowSuggestionResponse) {
    const name = decodeWorkflowSuggestion(typeof item === 'string' ? item : item.name)
    return {value: name, displayName: name}
  }
}

export function isWorkflowRunWorkflowSuggestion(value: unknown): value is WorkflowRunWorkflowSuggestion {
  return (
    typeof value === 'object' &&
    value !== null &&
    'name' in value &&
    typeof value.name === 'string' &&
    'pinned' in value &&
    typeof value.pinned === 'boolean'
  )
}

// Names arrive pre-quoted (`Search::ParsedQuery.encode_value`). Unwrap to the raw name so the
// consumer owns query escaping; parsing the value as a full qualifier would split names containing commas.
export function decodeWorkflowSuggestion(item: string) {
  const unquoted = item.startsWith('"') && item.endsWith('"') ? item.slice(1, -1) : item
  return unquoted.replaceAll('\\"', '"')
}

export class EventSuggestionsFilterProvider extends WorkflowRunsAsyncFilterProvider<string> {
  protected toSuggestion(item: string) {
    return {value: item, displayName: item}
  }
}

export class StatusSuggestionsFilterProvider extends WorkflowRunsAsyncFilterProvider<string> {
  protected toSuggestion(item: string) {
    return {value: item, displayName: capitalize(item.replaceAll('_', ' '))}
  }
}

export interface WorkflowRunBranchSuggestion {
  name: string
  default: boolean
}

export class BranchSuggestionsFilterProvider extends WorkflowRunsAsyncFilterProvider<WorkflowRunBranchSuggestion> {
  protected toSuggestion(item: WorkflowRunBranchSuggestion) {
    return {value: item.name, displayName: item.default ? `${item.name} (default)` : item.name}
  }
}

export interface WorkflowRunActorSuggestion {
  login: string
  displayName: string | null
  avatarUrl: string
}

export class ActorSuggestionsFilterProvider extends WorkflowRunsAsyncFilterProvider<WorkflowRunActorSuggestion> {
  // The login is what gets inserted into the query and is the only unique identifier, so it stays
  // the primary text; display names are not unique and are shown as a secondary description.
  // Mirrors `BaseUserFilterProvider`.
  protected toSuggestion(item: WorkflowRunActorSuggestion) {
    return {
      value: item.login,
      displayName: item.login,
      description: item.displayName && item.displayName !== item.login ? item.displayName : undefined,
    }
  }
}
